import React from 'react';
import { useTranslation } from 'react-i18next';

const icons = {
  web: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
      <rect x="3" y="4" width="18" height="14" rx="2" /><path d="M3 9h18M8 21h8M12 18v3" />
    </svg>
  ),
  app: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
      <rect x="7" y="2" width="10" height="20" rx="2.5" /><path d="M11 18h2" />
    </svg>
  ),
  hosting: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
      <rect x="3" y="4" width="18" height="6" rx="2" /><rect x="3" y="14" width="18" height="6" rx="2" /><path d="M7 7h.01M7 17h.01" />
    </svg>
  ),
  consult: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /><path d="M8 9h8M8 13h5" />
    </svg>
  ),
};

const ServiceCard = ({ icon, title, description }) => (
  <div className="group relative flex flex-col rounded-2xl border border-slatex-200 bg-white p-7 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-brand-300 hover:shadow-card">
    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 transition-colors duration-300 group-hover:bg-brand-600 group-hover:text-white">
      {icon}
    </span>
    <h3 className="mt-5 text-lg font-bold text-ink font-display">{title}</h3>
    <p className="mt-3 text-sm leading-relaxed text-slatex-600">{description}</p>
  </div>
);

const ServicesComponent = () => {
  const { t } = useTranslation();

  const services = [
    { icon: icons.web, title: t('services.webDevelopment.title'), description: t('services.webDevelopment.description') },
    { icon: icons.app, title: t('services.mobileAppDevelopment.title'), description: t('services.mobileAppDevelopment.description') },
    { icon: icons.hosting, title: t('services.domainHosting.title'), description: t('services.domainHosting.description') },
    { icon: icons.consult, title: t('services.itConsultations.title'), description: t('services.itConsultations.description') },
  ];

  return (
    <div id="services" className="bg-slatex-50 py-20 lg:py-28">
      <section className="container-x">
        <div className="text-center max-w-2xl mx-auto" data-aos="fade-up">
          <span className="eyebrow justify-center">{t('ui.eyebrowServices')}</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-ink">{t('services.title')}</h2>
          <p className="mt-4 text-slatex-600">{t('services.subtitle')}</p>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-aos="fade-up" data-aos-delay="150">
          {services.map((s) => (
            <ServiceCard key={s.title} {...s} />
          ))}
        </div>
      </section>

      {/* Build / Collaborate */}
      <section className="container-x mt-20 lg:mt-28">
        <div className="grid gap-10 lg:grid-cols-2" data-aos="fade-up">
          {[
            {
              title: t('services.build.title'),
              description: t('services.build.description'),
              icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7"><path d="M12 2 2 7l10 5 10-5-10-5Z" /><path d="m2 17 10 5 10-5M2 12l10 5 10-5" /></svg>
              ),
            },
            {
              title: t('services.collaborate.title'),
              description: t('services.collaborate.description'),
              icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
              ),
            },
          ].map((item) => (
            <div key={item.title} className="rounded-2xl border border-slatex-200 bg-white p-8 shadow-soft">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ink text-white">{item.icon}</span>
              <h3 className="mt-6 text-2xl font-bold text-ink">{item.title}</h3>
              <p className="mt-3 text-slatex-600 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ServicesComponent;
